import type Store from "@ember-data/store";
import { action } from "@ember/object";
import { tracked } from "@glimmer/tracking";
import type Patient from "parog-web/models/patient";
import Ember from "ember";
import { task } from "ember-concurrency";
import type { DmpAdapter } from "parog-web/cnam-adapters/dmp-adapter";
import type { InsiAdapter } from "parog-web/cnam-adapters/insi-adapter";
import type DmpBulkDownloadSessionStartedJournalEvent from "parog-web/models/dmp-bulk-download-session-started-journal-event";
import type FetcherService from "parog-web/services/fetcher";
import { CnamApiError } from "parog-web/utils/cnam-api-errors";
import ControlTower from "wise-control-tower";
import type { AuthenticationContext } from "./authentication-context";
import DmpRetrievalContext from "./dmp-retrieval-context";

class DmpBulkDownloadDmpRetrievalContext extends DmpRetrievalContext {
  constructor(
    cnamAdapterName: string,
    patient: Patient,
    authenticationContext: AuthenticationContext,
    dmpAdapter: DmpAdapter,
    insiAdapter: InsiAdapter,
    dmpBulkDownloadSessionStartedJournalEvent: DmpBulkDownloadSessionStartedJournalEvent,
    store: Store,
    fetcher: FetcherService
  ) {
    super(
      cnamAdapterName,
      patient,
      authenticationContext,
      dmpAdapter,
      insiAdapter,
      store,
      fetcher,
      () => {
        dmpBulkDownloadSessionStartedJournalEvent.cnamProcessStartedJournalEvents.push(
          this.cnamProcessStartedJournalEvent
        );
        dmpBulkDownloadSessionStartedJournalEvent.save();
      }
    );
    // We disable secret connection handling for bulk download
    // This is a consequence of DMPConnectJS secret connection handling :
    // Setting a secret connection with DMPConnectJS requires a function call
    // stating that all the future calls will be done with a secret
    // connection. As multiple calls may be done concurrently, it would
    // require us to ensure that "setting a secret connection + an actual TD call"
    // is atomically done. It would require another refactoring, so we'll see
    // in a future release.

    // We also disable the practice location selection
    // in case the currently selected isn't vaid, because we can't
    // show an intermediate step for the moment.

    // We disable the retrieval of non-HR
    // documents, as it's too long
    // and too much error prone.
    this.states = this.states.filter(
      state =>
        state.key != "set-secret-connection" &&
        state.key != "select-another-practice-location-with-billing-number" &&
        state.key != "retrieve-and-save-documents"
    );

    const saveIdentityState = this.states.find(
      state => state.key == "save-identity"
    );
    if (saveIdentityState) {
      saveIdentityState.requiresUserAction = false;
    }

    // This is needed for the DMP retrieval state machine
    this.nonHrRetrieved = true;
    this.nonHrProcessEnded = true;
  }

  modifyStatus(row: BulkDownloadRow | undefined): void {
    if (!row) return;
    if (row.context.lastError) {
      row.status = "failure";
    } else if (row.context.successKey) {
      row.status = "successful";
    } else {
      switch (row.context.currentState!.key) {
        case "set-secret-connection":
          row.status = "secret-connection-to-set";
          break;
        case "ins-qualification-needed":
          row.status = "ins-not-qualified";
          break;
        case "authorize-medic":
          row.status = "unauthorized-medic-no";
          break;
        case "retrieve-and-save-latest-reimbursement-document":
          row.status = "retrieving";
          break;
      }
    }
  }

  transitionTo = async (row?: BulkDownloadRow): Promise<void> => {
    this.loading = true;
    await this._transitionTo(row);
    this.loading = false;
  };

  _transitionTo = async (row?: BulkDownloadRow): Promise<void> => {
    try {
      await this.currentState?.run();
      this.currentState = await this.calculateCurrentState();
      this.modifyStatus(row);
      if (
        this.currentState &&
        !this.currentState?.requiresUserAction &&
        !this.successKey
      ) {
        await this._transitionTo(row);
      }
    } catch (error) {
      if (!Ember.testing) console.error("Error in context", error);

      if (error instanceof CnamApiError && !Ember.testing) {
        console.error("Details", error.error);
        ControlTower.captureException(error);
      }

      this.lastError = error;
      this.modifyStatus(row);
    }
  };
}

export class BulkDownloadRow {
  @tracked status: BulkDownloadRowStatus;
  @tracked context: DmpBulkDownloadDmpRetrievalContext;
  @tracked selected: boolean;

  constructor(
    public patient: Patient,
    status: BulkDownloadRowStatus,
    context: DmpBulkDownloadDmpRetrievalContext,
    selected = false
  ) {
    this.status = status;
    this.context = context;
    this.selected = selected;
  }
}

type BulkDownloadRowStatus =
  | "to-treat"
  | "unselected-to-treat"
  | "ineligible"
  | "retrieving"
  | "successful"
  | "secret-connection-to-set"
  | "unauthorized-medic-yes"
  | "unauthorized-medic-no"
  | "unauthorized-medic-reject"
  | "ins-not-qualified"
  | "ins-qualification-failed"
  | "failure";

export default class DmpBulkDownloadContext {
  declare cnamAdapterName: string;
  declare insiAdapter: InsiAdapter;
  declare dmpAdapter: DmpAdapter;
  declare authenticationContext: AuthenticationContext;
  declare store: Store;
  declare fetcher: FetcherService;
  declare dmpBulkDownloadSessionStartedJournalEvent: DmpBulkDownloadSessionStartedJournalEvent;

  @tracked bulkDownloadRows: BulkDownloadRow[] = [];

  constructor(
    cnamAdapterName: string,
    eligibleSortedPatientsToTreat: Patient[],
    notEligibleButEnrolledPatientsToTreat: Patient[],
    insiAdapter: InsiAdapter,
    dmpAdapter: DmpAdapter,
    authenticationContext: AuthenticationContext,
    store: Store,
    fetcher: FetcherService,
    dmpBulkDownloadSessionStartedJournalEvent: DmpBulkDownloadSessionStartedJournalEvent
  ) {
    this.cnamAdapterName = cnamAdapterName;
    this.insiAdapter = insiAdapter;
    this.dmpAdapter = dmpAdapter;
    this.authenticationContext = authenticationContext;
    this.store = store;
    this.fetcher = fetcher;

    eligibleSortedPatientsToTreat.forEach((patient, index) => {
      this.bulkDownloadRows.push(
        new BulkDownloadRow(
          patient,
          "to-treat",
          new DmpBulkDownloadDmpRetrievalContext(
            this.cnamAdapterName,
            patient,
            this.authenticationContext,
            this.dmpAdapter,
            this.insiAdapter,
            dmpBulkDownloadSessionStartedJournalEvent,
            this.store,
            this.fetcher
          ),
          index < this.dmpAdapter.remainingRate
        )
      );
    });

    /** Only enrolled patients are shown */
    notEligibleButEnrolledPatientsToTreat.forEach(patient => {
      this.bulkDownloadRows.push(
        new BulkDownloadRow(
          patient,
          "ineligible",
          new DmpBulkDownloadDmpRetrievalContext(
            this.cnamAdapterName,
            patient,
            this.authenticationContext,
            this.dmpAdapter,
            this.insiAdapter,
            dmpBulkDownloadSessionStartedJournalEvent,
            this.store,
            this.fetcher
          )
        )
      );
    });
  }

  @action
  reset() {
    this.bulkDownloadRows = [];
  }

  filterGivenStatus = (
    patients: BulkDownloadRow[],
    status: string
  ): BulkDownloadRow[] => {
    return patients.filter(patient => patient.status === status);
  };

  get patientsTreating(): BulkDownloadRow[] {
    return this.filterGivenStatus(this.bulkDownloadRows, "retrieving");
  }

  get patientsToTreat(): BulkDownloadRow[] {
    return this.filterGivenStatus(this.bulkDownloadRows, "to-treat");
  }

  get selectedPatientsToTreat(): BulkDownloadRow[] {
    return this.patientsToTreat.filter(row => row.selected);
  }

  get unselectedPatientsToTreat(): BulkDownloadRow[] {
    return this.patientsToTreat.filter(row => !row.selected);
  }

  get unselectedPatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(this.bulkDownloadRows, "unselected-to-treat");
  }

  get hourlyRateOnError(): boolean {
    return (
      this.selectedPatientsToTreat.length > this.dmpAdapter.hourlyRemainingRate
    );
  }

  get dailyRateOnError(): boolean {
    return (
      this.selectedPatientsToTreat.length > this.dmpAdapter.dailyRemainingRate
    );
  }

  get successfulPatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(this.bulkDownloadRows, "successful");
  }

  get unauthorizedMedicToTreatPatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(
      this.bulkDownloadRows,
      "unauthorized-medic-yes"
    );
  }

  get unauthorizedMedicToRejectPatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(
      this.bulkDownloadRows,
      "unauthorized-medic-no"
    );
  }

  get unauthorizedMedicToDecidePatients(): BulkDownloadRow[] {
    return [
      ...this.unauthorizedMedicToTreatPatients,
      ...this.unauthorizedMedicToRejectPatients
    ].toSorted(this.sortOnFirstAndLastName);
  }

  get unauthorizedMedicRejectedPatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(
      this.bulkDownloadRows,
      "unauthorized-medic-reject"
    );
  }

  get insNotQualifiedInsPatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(this.bulkDownloadRows, "ins-not-qualified");
  }

  get insQualificationFailedPatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(
      this.bulkDownloadRows,
      "ins-qualification-failed"
    );
  }

  get secretConnectionToSetPatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(
      this.bulkDownloadRows,
      "secret-connection-to-set"
    );
  }

  get ineligiblePatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(this.bulkDownloadRows, "ineligible");
  }

  get failurePatients(): BulkDownloadRow[] {
    return this.filterGivenStatus(this.bulkDownloadRows, "failure");
  }

  sortOnFirstAndLastName(a: BulkDownloadRow, b: BulkDownloadRow): number {
    const aa = a.patient.firstName + ", " + a.patient.lastName,
      bb = b.patient.firstName + ", " + b.patient.lastName;
    if (aa > bb) return 1;
    else if (aa < bb) return -1;
    return 0;
  }

  treatInsQualifications = task(async () => {
    for (const insNotQualifiedInsPatient of this.insNotQualifiedInsPatients) {
      if (insNotQualifiedInsPatient.context.insDocumentIsQualifying) {
        await insNotQualifiedInsPatient.context.transitionTo(
          insNotQualifiedInsPatient
        );
      } else {
        insNotQualifiedInsPatient.status = "ins-qualification-failed";
      }
    }
  });

  treatAuthorizeMedics = task(async () => {
    for (const unauthorizedMedicToRejectPatient of this
      .unauthorizedMedicToRejectPatients) {
      unauthorizedMedicToRejectPatient.status = "unauthorized-medic-reject";
    }

    for (const unauthorizedMedicToTreatPatient of this
      .unauthorizedMedicToTreatPatients) {
      unauthorizedMedicToTreatPatient.context.shouldAuthorizeMedicToAccessPatientDmp =
        true;
      await unauthorizedMedicToTreatPatient.context.transitionTo(
        unauthorizedMedicToTreatPatient
      );
    }
  });

  setSecretConnections = task(async () => {
    for (const secretConnectionToSetPatient of this
      .secretConnectionToSetPatients) {
      await secretConnectionToSetPatient.context.transitionTo(
        secretConnectionToSetPatient
      );
    }
  });

  retrievePatientsDmp = task(async () => {
    for (const unselectedPatientToTreat of this.unselectedPatientsToTreat) {
      unselectedPatientToTreat.status = "unselected-to-treat";
    }

    for (const selectedPatientToTreat of this.selectedPatientsToTreat) {
      selectedPatientToTreat.status = "retrieving";
      await selectedPatientToTreat.context.transitionTo(selectedPatientToTreat);
    }
  });
}
